import { styled, css, Button, keyframes, alpha, Box, BoxProps } from '@mui/material'
import {
  SideLabelProps,
  ButtonStylesProps,
  ButtonInternalProps,
  ButtonProps
} from './Button.types'
import Price from '../Price'
import getPaletteColor from '../../../utils/getPaletteColor'

export const ButtonStyled = styled(Button, {
  shouldForwardProp: (prop) =>
    !['hasStartLabel', 'hasEndLabel', 'actions'].includes(String(prop))
})<ButtonStylesProps & ButtonInternalProps>(
  ({
    theme,
    size,
    radius,
    color = 'primary',
    variant,
    fullWidth,
    hasStartLabel,
    hasEndLabel,
    disabled,
    textTransform = 'uppercase'
  }) => css`
    position: relative;
    overflow: hidden;
    justify-content: ${hasStartLabel || hasEndLabel
      ? 'space-between'
      : 'center'};
    gap: 8px;
    line-height: 2em;
    font-weight: 600;
    text-transform: ${textTransform};

    ${radius !== undefined &&
    css`
      border-radius: ${typeof radius === 'string' ? radius : `${radius}px`};
    `}

    ${size === 'small' &&
    css`
      font-size: 12px;
      padding: 4px 12px;
    `}

    ${size === 'medium' &&
    css`
      font-size: 13px;
      padding: 7px 16px;
    `}

    ${size === 'large' &&
    css`
      font-size: 14px;
      padding: 10px 16px;
    `}

    ${hasStartLabel &&
    hasEndLabel &&
    fullWidth &&
    css`
      .LabelMain {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
    `}
    
    ${color && color !== 'inherit' && !disabled
      ? css`
          > * {
            border-color: ${theme.palette[color][
        variant === 'contained' ? 'contrastText' : 'main'
        ]};
          }
        `
      : css`
          border-color: ${theme.palette.grey[300]};
          > * {
            border-color: ${theme.palette.text.secondary};
          }
        `}
    ${disabled &&
    css`
      > * {
        border-color: ${theme.palette.text.disabled};
      }
    `}
  `
)
ButtonStyled.defaultProps = {
  variant: 'contained',
  color: 'primary',
  size: 'medium'
}

export const ButtonLabel = styled(Box)(
  () => css`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    white-space: nowrap;
    text-transform: inherit;

    .Icon {
      font-size: 1.4em;
    }
  `
)
ButtonLabel.defaultProps = {
  component: 'span'
} as Partial<BoxProps>

export const ButtonSideLabel = styled(Box, {
  shouldForwardProp: (prop) => !['counter'].includes(String(prop))
})<SideLabelProps>(
  ({ counter }) => css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 2em;
    min-width: 2em;
    padding: 4px;
    font-size: 1em;
    font-weight: normal;
    border-radius: 24px;
    box-sizing: border-box;
    white-space: nowrap;

    ${counter &&
    css`
      border-width: 1px;
      border-style: solid;
      font-weight: bold;
    `}
  `
)
ButtonSideLabel.defaultProps = {
  component: 'span'
} as Partial<BoxProps>

export const LabelPrice = styled(Price)(
  () => css`
    font-weight: normal;
  `
)
LabelPrice.defaultProps = {
  color: 'inherit'
}

const rotation = keyframes`
  to {
    transform: rotate(360deg);
  }
`

export const LoaderIcon = styled('div')(
  () => css`
    width: 18px;
    height: 18px;
    border: 2px solid;
    border-color: inherit;
    border-bottom-color: transparent;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: ${rotation} 0.8s linear infinite;
  `
)

export const LoaderWrapper = styled('div', {
  shouldForwardProp: (prop) =>
    !['loading', 'loadingOpacity'].includes(String(prop))
})<Pick<ButtonProps, 'color' | 'loading' | 'loadingOpacity'>>(
  ({ theme, color, loading, loadingOpacity = 0.6 }) => css`
    opacity: ${loading ? 1 : 0};
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${color === 'inherit'
      ? `rgba(255, 255, 255, ${loadingOpacity})`
      : alpha(getPaletteColor(theme, color), loadingOpacity)};
    color: inherit;
    border-color: ${getPaletteColor(theme, color, 'contrastText')};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;
  `
)
